import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { UserCheck, UserX, Mail, Github, Clock, ShieldAlert, Trash2, Users, ShieldCheck, ShieldX, UploadCloud, Eye, Activity, Share2, Edit2, PlusCircle, AlertCircle, Key, FileText, Download } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import Navbar from '../components/Navbar';

const AdminPage = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('users');
  const [actionUser, setActionUser] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [updating, setUpdating] = useState(null); // userId being updated
  const [approvePerms, setApprovePerms] = useState({ can_upload: 1, can_delete: 1, can_view_all: 0, can_manage_users: 0, can_view_logs: 0 });
  const [rejectionReason, setRejectionReason] = useState('Standard eligibility criteria not met');
  const { toast } = useToast();

  React.useEffect(() => {
    fetchPendingUsers();
    fetchLogs();
    fetchAllUsers();
  }, []);

  const fetchAllUsers = async () => {
    try {
      const { adminApi } = await import('../lib/api');
      const users = await adminApi.getAllUsers();
      setAllUsers(users);
    } catch (error) {
      console.error('Failed to fetch all users:', error);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const { adminApi } = await import('../lib/api');
      const users = await adminApi.getPendingUsers();
      setPendingUsers(users);
    } catch (error) {
      console.error('Failed to fetch pending users:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      const { adminApi } = await import('../lib/api');
      const logs = await adminApi.getLogs();
      setLogs(logs);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    }
  };

  const handleApprove = async (user, perms = {}) => {
    try {
      const { adminApi } = await import('../lib/api');
      await adminApi.approveUser(user.id, perms);
      setPendingUsers(pendingUsers.filter(u => u.id !== user.id));
      setActionUser(null);
      setActionType(null);
      fetchLogs(); // Refresh logs
      fetchAllUsers(); // Refresh all users for management tab
      toast({
        title: 'User approved',
        description: `${user.name} has been approved.`
      });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to approve user', variant: 'destructive' });
    }
  };

  const handleReject = async (user, reason) => {
    try {
      const { adminApi } = await import('../lib/api');
      await adminApi.rejectUser(user.id, reason);
      setPendingUsers(pendingUsers.filter(u => u.id !== user.id));
      setActionUser(null);
      setActionType(null);
      fetchLogs(); // Refresh logs
      fetchAllUsers(); // Refresh all users
      toast({
        title: 'User rejected',
        description: `${user.name} has been rejected.`,
        variant: 'destructive'
      });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to reject user', variant: 'destructive' });
    }
  };

  const handleDelete = async (user) => {
    try {
      const { adminApi } = await import('../lib/api');
      await adminApi.deleteUser(user.id);
      setPendingUsers(pendingUsers.filter(u => u.id !== user.id));
      setActionUser(null);
      setActionType(null);
      fetchLogs(); // Refresh logs
      fetchAllUsers(); // Refresh all users
      toast({
        title: 'User deleted',
        description: `${user.name} has been permanently deleted.`,
        variant: 'destructive'
      });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete user', variant: 'destructive' });
    }
  };

  const handleTogglePermission = async (user, permission) => {
    setUpdating(user.id);
    try {
      const { adminApi } = await import('../lib/api');
      const currentValue = user[permission];
      // Loosely check for truthy value to handle 1, "1", true
      const newValue = (currentValue == 1 || currentValue === true) ? 0 : 1;

      await adminApi.updatePermissions(user.id, { [permission]: newValue });

      setAllUsers(allUsers.map(u => u.id === user.id ? { ...u, [permission]: newValue } : u));

      toast({
        title: 'Permission updated',
        description: `Updated ${permission} for ${user.name}`
      });
      fetchLogs(); // Refresh logs to show update
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update permission', variant: 'destructive' });
    } finally {
      setUpdating(null);
    }
  };

  const formatDate = (dateString, full = false) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: full ? '2-digit' : undefined
    });
  };

  const getIconForAction = (action) => {
    switch (action.toLowerCase()) {
      case 'approve': return <UserCheck className="w-4 h-4" />;
      case 'reject': return <UserX className="w-4 h-4" />;
      case 'delete': return <Trash2 className="w-4 h-4" />;
      case 'update_permissions': return <ShieldCheck className="w-4 h-4" />;
      case 'request_access': return <Key className="w-4 h-4" />;
      case 'file_upload': return <UploadCloud className="w-4 h-4" />;
      case 'file_download': return <Download className="w-4 h-4" />;
      case 'file_delete': return <FileText className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getActionColor = (action) => {
    switch (action.toLowerCase()) {
      case 'approve': return "bg-emerald-500/10 text-emerald-600 border-emerald-500/20";
      case 'reject': return "bg-orange-500/10 text-orange-600 border-orange-500/20";
      case 'delete': return "bg-rose-500/10 text-rose-600 border-rose-500/20";
      case 'update_permissions': return "bg-blue-500/10 text-blue-600 border-blue-500/20";
      case 'request_access': return "bg-purple-500/10 text-purple-600 border-purple-500/20";
      case 'file_upload': return "bg-teal-500/10 text-teal-600 border-teal-500/20";
      case 'file_download': return "bg-indigo-500/10 text-indigo-600 border-indigo-500/20";
      case 'file_delete': return "bg-rose-500/10 text-rose-600 border-rose-500/20";
      default: return "bg-gray-500/10 text-gray-600 border-gray-500/20";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Manage users and review system security logs</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white p-1 rounded-lg border border-gray-200">
            <TabsTrigger value="users" className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
              <UserCheck className="w-4 h-4 mr-2" />
              Pending Approvals
            </TabsTrigger>
            <TabsTrigger value="manage" className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
              <Users className="w-4 h-4 mr-2" />
              User Management
            </TabsTrigger>
            <TabsTrigger value="logs" className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
              <ShieldAlert className="w-4 h-4 mr-2" />
              Security Logs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <Card className="p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-1">Pending Approvals</h2>
                <p className="text-sm text-gray-500">
                  {pendingUsers.length} {pendingUsers.length === 1 ? 'user' : 'users'} waiting for approval
                </p>
              </div>

              {pendingUsers.length === 0 ? (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4">
                    <UserCheck className="h-8 w-8 text-teal-600" />
                  </div>
                  <p className="text-gray-600 font-medium mb-1">All caught up!</p>
                  <p className="text-sm text-gray-500">No pending user approvals at the moment.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>GitHub</TableHead>
                        <TableHead>Requested At</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <div className="flex items-center">
                              <img src={user.avatar_url} alt={user.name} className="w-10 h-10 rounded-full mr-3" />
                              <div>
                                <p className="font-medium text-gray-900">{user.name}</p>
                                <p className="text-sm text-gray-500">@{user.username}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center text-sm text-gray-600">
                              <Mail className="h-4 w-4 mr-2 text-gray-400" />
                              {user.email}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center text-sm text-gray-600">
                              <Github className="h-4 w-4 mr-2 text-gray-400" />
                              {user.username}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center text-sm text-gray-600">
                              <Clock className="h-4 w-4 mr-2 text-gray-400" />
                              {formatDate(user.created_at)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Button
                                size="sm"
                                onClick={() => { setActionUser(user); setActionType('approve'); }}
                                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
                              >
                                <UserCheck className="h-4 w-4 mr-1" /> Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => { setActionUser(user); setActionType('reject'); }}
                              >
                                <UserX className="h-4 w-4 mr-1" /> Reject
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                className="bg-red-800 hover:bg-red-900"
                                onClick={() => { setActionUser(user); setActionType('delete'); }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="manage">
            <Card className="p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-1">User Management</h2>
                <p className="text-sm text-gray-500">Manage granular permissions for all approved users</p>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead className="text-center">Upload</TableHead>
                      <TableHead className="text-center">Delete</TableHead>
                      <TableHead className="text-center">View All</TableHead>
                      <TableHead className="text-center">Manage</TableHead>
                      <TableHead className="text-center">Logs</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allUsers.filter(u => u.is_approved === 1 || u.is_approved === -1).map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div className="flex items-center">
                            <img src={user.avatar_url} alt={user.name} className="w-10 h-10 rounded-full mr-3 border border-gray-200" />
                            <div>
                              <p className="font-medium text-gray-900 flex items-center">
                                {user.name}
                                {user.is_admin === 1 && <ShieldCheck className="h-3 w-3 ml-1.5 text-orange-500" />}
                              </p>
                              <p className="text-xs text-gray-500">{user.email}</p>
                              {user.is_approved === -1 && user.rejection_reason && (
                                <p className="text-[10px] text-red-500 mt-1 italic">Reason: {user.rejection_reason}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={updating === user.id}
                            onClick={() => handleTogglePermission(user, 'can_upload')}
                            className={`rounded-full h-8 w-8 p-0 ${user.can_upload ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                          >
                            <UploadCloud className="h-4 w-4" />
                          </Button>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={updating === user.id}
                            onClick={() => handleTogglePermission(user, 'can_delete')}
                            className={`rounded-full h-8 w-8 p-0 ${user.can_delete ? 'bg-red-100 text-red-600 hover:bg-red-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={updating === user.id}
                            onClick={() => handleTogglePermission(user, 'can_view_all')}
                            className={`rounded-full h-8 w-8 p-0 ${user.can_view_all ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={updating === user.id}
                            onClick={() => handleTogglePermission(user, 'can_manage_users')}
                            className={`rounded-full h-8 w-8 p-0 ${user.can_manage_users ? 'bg-amber-100 text-amber-600 hover:bg-amber-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                          >
                            <Users className="h-4 w-4" />
                          </Button>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={updating === user.id}
                            onClick={() => handleTogglePermission(user, 'can_view_logs')}
                            className={`rounded-full h-8 w-8 p-0 ${user.can_view_logs ? 'bg-purple-100 text-purple-600 hover:bg-purple-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                          >
                            <Activity className="h-4 w-4" />
                          </Button>
                        </TableCell>
                        <TableCell>
                          {user.is_approved === 1 ? (
                            <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded-full">
                              Active
                            </span>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span className="text-xs font-medium text-red-700 bg-red-50 px-2 py-1 rounded-full">
                                Rejected
                              </span>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs border-green-200 text-green-700 hover:bg-green-50 px-2"
                                onClick={() => handleApprove(user)}
                              >
                                Re-approve
                              </Button>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => { setActionUser(user); setActionType('delete'); }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {allUsers.filter(u => u.is_approved === 1 || u.is_approved === -1).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                          No users found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="logs">
            <Card className="p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-1">Security Logs</h2>
                <p className="text-sm text-gray-500">Audit trail of all administrative actions</p>
              </div>
              <div className="space-y-4">
                {logs.length === 0 ? (
                  <div className="p-16 text-center text-gray-500 italic font-medium">
                    No activity logged yet
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden bg-white shadow-sm">
                    {logs.map((log) => {
                      let detailsObj = {};
                      try {
                        detailsObj = typeof log.details === 'string' ? JSON.parse(log.details) : log.details || {};
                      } catch (e) {
                        detailsObj = { raw: log.details };
                      }

                      return (
                        <div key={log.id} className="p-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors">
                          <div className="flex items-center gap-4">
                            <div className={`p-2.5 rounded-xl shadow-sm border ${getActionColor(log.action)}`}>
                              {getIconForAction(log.action)}
                            </div>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-sm text-gray-900">
                                  {log.admin_username ? `@${log.admin_username}` : log.admin_user_id ? `@${log.admin_user_id.substring(0, 8)}` : 'System'}
                                </span>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                  {log.action.replace('_', ' ')}
                                </span>
                                {detailsObj.filename && (
                                  <span className="font-bold text-sm text-primary/80">
                                    {detailsObj.filename}
                                  </span>
                                )}
                                {(log.target_username || log.target_user_id || detailsObj.owner_id) && (
                                  <>
                                    <span className="text-gray-300 text-xs">for</span>
                                    <span className="font-bold text-sm text-teal-600">
                                      {log.target_username ? `@${log.target_username}` : `@${(log.target_user_id || detailsObj.owner_id).substring(0, 8)}`}
                                    </span>
                                  </>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <p className="text-[10px] text-gray-400 font-bold">
                                  {formatDate(log.timestamp * 1000, true)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {actionUser && (
          <AlertDialog open={!!actionUser} onOpenChange={() => { setActionUser(null); setActionType(null); }}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {actionType === 'approve' ? 'Approve User' : actionType === 'reject' ? 'Reject User' : 'Delete User'}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {actionType === 'approve' && <span>Are you sure you want to approve <strong>{actionUser.name}</strong>?</span>}
                  {actionType === 'reject' && (
                    <div className="space-y-3">
                      <span>Are you sure you want to reject <strong>{actionUser.name}</strong>?</span>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-gray-500">Rejection Reason</label>
                        <textarea
                          className="w-full min-h-[80px] p-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-teal-500 outline-none"
                          placeholder="Why is this user being rejected?"
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                  {actionType === 'delete' && (
                    <div className="space-y-3">
                      <span>Are you sure you want to <strong>permanently delete</strong> {actionUser.name}?</span>
                      <div className="p-3 bg-red-50 border border-red-100 rounded-md text-red-700 text-xs">
                        <AlertCircle className="h-4 w-4 mb-1" />
                        <strong>Warning:</strong> This will also delete all files and metadata associated with this user. This action cannot be undone.
                      </div>
                    </div>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              {actionType === 'approve' && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 py-2">
                  <button
                    className={`border rounded-md px-3 py-2 text-sm ${approvePerms.can_upload ? 'border-green-300 bg-green-50 text-green-700' : 'border-gray-200 text-gray-700'}`}
                    onClick={() => setApprovePerms(p => ({ ...p, can_upload: p.can_upload ? 0 : 1 }))}
                  >
                    Upload
                  </button>
                  <button
                    className={`border rounded-md px-3 py-2 text-sm ${approvePerms.can_delete ? 'border-rose-300 bg-rose-50 text-rose-700' : 'border-gray-200 text-gray-700'}`}
                    onClick={() => setApprovePerms(p => ({ ...p, can_delete: p.can_delete ? 0 : 1 }))}
                  >
                    Delete
                  </button>
                  <button
                    className={`border rounded-md px-3 py-2 text-sm ${approvePerms.can_view_all ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-700'}`}
                    onClick={() => setApprovePerms(p => ({ ...p, can_view_all: p.can_view_all ? 0 : 1 }))}
                  >
                    View All
                  </button>
                  <button
                    className={`border rounded-md px-3 py-2 text-sm ${approvePerms.can_manage_users ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-700'}`}
                    onClick={() => setApprovePerms(p => ({ ...p, can_manage_users: p.can_manage_users ? 0 : 1 }))}
                  >
                    Manage Users
                  </button>
                  <button
                    className={`border rounded-md px-3 py-2 text-sm ${approvePerms.can_view_logs ? 'border-purple-300 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-700'}`}
                    onClick={() => setApprovePerms(p => ({ ...p, can_view_logs: p.can_view_logs ? 0 : 1 }))}
                  >
                    Security Logs
                  </button>
                </div>
              )}
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    if (actionType === 'approve') handleApprove(actionUser, approvePerms);
                    else if (actionType === 'reject') handleReject(actionUser, rejectionReason);
                    else if (actionType === 'delete') handleDelete(actionUser);
                  }}
                  className={
                    actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                      'bg-red-600 hover:bg-red-700'
                  }
                >
                  Confirm
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
